import React, { useState, useEffect, useRef } from 'react';
import { styles } from '../../../utils/styles';
import { OverFlowIcon, SubModelIcon } from 'assests/GmsIcons';
import { useEditor } from '../../../contexts/EditorContext';
import { useComponentRegistry } from '../../../contexts/ComponentRegistryContext';
import OverlayIcon from 'Components/GMS/Rules/Components/OverlayIcon';

type SubmodelConfigProps = {
    id?: string;
    submodelId: string;
    submodelName?: string;
    onConfigChange?: (config: any) => void;
}

export const SubmodelConfig = ({ id, submodelId, submodelName,onConfigChange }: SubmodelConfigProps) => {
    const baseId = id || 'submodel-config';
    const { containers, editorState, setContainers } = useEditor();
    const { getComponentDefinition } = useComponentRegistry();

    const submodelDef = getComponentDefinition(submodelId);

    // Debouncing for display name input
    const displayNameDebounceRef = useRef<NodeJS.Timeout>();

    const [showTitle, setShowTitle] = useState(true);
    const [fieldName, setFieldName] = useState(submodelDef?.name || submodelName || '');
    const [displayName, setDisplayName] = useState('');
    const [localDisplayName, setLocalDisplayName] = useState('');
    const [hideIfNoProducts, setHideIfNoProducts] = useState(false);
    const [exclusionType, setExclusionType] = useState<'hide' | 'disable'>('hide');
    
    useEffect(() => {
        if (editorState.activePageId && containers[editorState.activePageId]) {
            const pageContainers = containers[editorState.activePageId];
            
            const findSubmodelConfig = (containerList: any[]): any => {
                for (const container of containerList) {
                    if (container.content?.submodel?.id === submodelId) {
                        return container.content.submodel;
                    }
                    
                    if (container.content?.nestedContainers?.length > 0) {
                        const found = findSubmodelConfig(container.content.nestedContainers);
                        if (found) return found;
                    }
                }
                return null;
            };
            
            const submodelConfig = findSubmodelConfig(pageContainers);
            if (submodelConfig) {
                setFieldName(submodelConfig.configuration?.fieldName || submodelConfig.name || submodelDef?.name || '');

                if (submodelConfig.configuration) {
                    setShowTitle(submodelConfig.configuration.showTitle ?? true);
                    const newDisplayName = submodelConfig.configuration.displayName || '';
                    setDisplayName(newDisplayName);
                    setLocalDisplayName(newDisplayName);
                    setHideIfNoProducts(submodelConfig.configuration.hideIfNoProducts ?? false);
                    setExclusionType(submodelConfig.configuration.exclusionType || 'hide');
                }
            }
        }
    }, [submodelId, editorState.activePageId, containers, submodelDef]);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (displayNameDebounceRef.current) {
                clearTimeout(displayNameDebounceRef.current);
            }
        };
    }, []);
    
    const handleDebouncedDisplayNameChange = (value: string) => {
        setLocalDisplayName(value);
        if (displayNameDebounceRef.current) {
            clearTimeout(displayNameDebounceRef.current);
        }
        displayNameDebounceRef.current = setTimeout(() => {
            handleConfigChange('displayName', value);
        }, 500);
    };

    const handleConfigChange = (key: string, value: any) => {
        switch (key) {
            case 'showTitle':
                setShowTitle(value);
                break;
            case 'displayName':
                setDisplayName(value);
                break;
            case 'hideIfNoProducts':
                setHideIfNoProducts(value);
                break;
            case 'exclusionType':
                setExclusionType(value);
                break;
        }
        
        if (editorState.activePageId) {
            setContainers(prev => {
                const newContainers = { ...prev };
                const pageContainers = [...(newContainers[editorState.activePageId ?? ''] || [])];
                
                const updateSubmodelConfig = (containerList: any[]): any[] => {
                    return containerList.map(container => {
                        if (container.content?.submodel?.id === submodelId) {
                            return {
                                ...container,
                                content: {
                                    ...container.content,
                                    submodel: {
                                        ...container.content.submodel,
                                        configuration: {
                                            ...container.content.submodel.configuration,
                                            [key]: value,
                                            fieldName: fieldName,
                                            displayName: key === 'displayName' ? value : displayName
                                        }
                                    }
                                }
                            };
                        }
                        
                        if (container.content?.nestedContainers?.length > 0) {
                            return {
                                ...container,
                                content: {
                                    ...container.content,
                                    nestedContainers: updateSubmodelConfig(container.content.nestedContainers)
                                }
                            };
                        }
                        
                        return container;
                    });
                };
                
                newContainers[editorState.activePageId ?? ''] = updateSubmodelConfig(pageContainers);
                return newContainers;
            });
        }
        
        if (onConfigChange) {
            onConfigChange({
                id: submodelId,
                showTitle,
                fieldName: fieldName,
                displayName: key === 'displayName' ? value : displayName,
                hideIfNoProducts: key === 'hideIfNoProducts' ? value : hideIfNoProducts,
                exclusionType: key === 'exclusionType' ? value : exclusionType,
                [key]: value
            });
        }
    };
    
    return (
        <div id={baseId} data-testid={baseId} className={styles.container}>
            <div id={`${baseId}-header`} className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-2 tw-border-b tw-border-[#D1D1D6] tw-mb-4">
                <div id={`${baseId}-title-wrapper`} className="tw-flex tw-items-center tw-gap-2">
                    <SubModelIcon />
                    <h2 id={`${baseId}-title`} className="tw-text-[#6D788C] tw-text-sm tw-font-semibold">
                        Submodel
                    </h2>
                </div>
                <button id={`${baseId}-menu-btn`} data-testid={`${baseId}-menu-btn`} className="tw-rounded tw-w-6 tw-h-6 tw-flex tw-items-center tw-justify-center">
                    <OverFlowIcon />
                </button>
            </div>

            <div id={`${baseId}-content`} className="tw-px-4 tw-py-1">
                {/* Field Name - Non-editable */}
                <div id={`${baseId}-name-section`} className="tw-mb-4">
                    <label id={`${baseId}-name-label`} className="tw-block tw-text-[#6D788C] tw-text-sm tw-mb-2">
                        Submodel Name
                    </label>
                    <input
                        id={`${baseId}-name-input`}
                        data-testid={`${baseId}-name-input`}
                        type="text"
                        value={fieldName}
                        disabled
                        className="tw-w-full tw-border tw-text-sm tw-text-[#172B4D] tw-font-medium tw-border-[#D1D1D6] tw-rounded tw-px-3 tw-py-2 tw-bg-[#F4F5F7] tw-cursor-not-allowed"
                        placeholder="Field name"
                    />
                </div>
                
                {/* Show Title Checkbox */}
                <div id={`${baseId}-show-title-section`} className="tw-mb-4">
                    <label id={`${baseId}-show-title-label`} className="tw-flex tw-items-center">
                        <input
                            id={`${baseId}-show-title-checkbox`}
                            data-testid={`${baseId}-show-title-checkbox`}
                            type="checkbox"
                            checked={showTitle}
                            onChange={(e) => handleConfigChange('showTitle', e.target.checked)}
                            className={styles.checkboxInput}
                        />
                        <span id={`${baseId}-show-title-text`} className={styles.checkboxLabel}>Show Title</span>
                    </label>

                    {/* Display Name Input - shown when Show Title is checked */}
                    {showTitle && (
                        <div id={`${baseId}-display-name-wrapper`} className="tw-mt-3">
                            <label id={`${baseId}-display-name-label`} className="tw-block tw-text-[#6D788C] tw-text-sm tw-mb-2 tw-ml-6">
                                Display Name
                            </label>
                            <div id={`${baseId}-display-name-input-wrapper`} className="tw-ml-6">
                                <input
                                    id={`${baseId}-display-name-input`}
                                    data-testid={`${baseId}-display-name-input`}
                                    type="text"
                                    value={localDisplayName}
                                    onChange={(e) => handleDebouncedDisplayNameChange(e.target.value)}
                                    className="tw-w-full tw-border tw-text-sm tw-text-[#172B4D] tw-font-medium tw-border-[#D1D1D6] tw-rounded tw-px-3 tw-py-2"
                                    placeholder="Enter display name"
                                />
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Hide category if no products */}
                <div id={`${baseId}-hide-section`} className="tw-mb-6 tw-border-b tw-border-[#D1D1D6] tw-border-t tw-py-2">
                    <label id={`${baseId}-hide-label`} className="tw-flex tw-items-center">
                        <input
                            id={`${baseId}-hide-checkbox`}
                            data-testid={`${baseId}-hide-checkbox`}
                            type="checkbox"
                            checked={hideIfNoProducts}
                            onChange={(e) => handleConfigChange('hideIfNoProducts', e.target.checked)}
                            className={styles.checkboxInput}
                        />
                        <span id={`${baseId}-hide-text`} className="tw-text-[#6D788C] tw-text-sm">
                            {'Hide, when no products are available'}
                        </span>
                        <div id={`${baseId}-hide-info-icon`} className="tw-ml-2 tw-h-5 tw-w-5 tw-flex tw-items-center tw-justify-center">
                            <OverlayIcon
                                message="When enabled, automatically hides the sections (Page/SubModel/Category) if no compatible products are available"
                                placement="bottom"
                            />
                        </div>
                    </label>
                </div>
                
                {/* Exclusions Section */}
                <div id={`${baseId}-exclusions-section`} className="tw-mb-4">
                    <h3 id={`${baseId}-exclusions-label`} className="tw-text-[#6D788C] tw-text-sm tw-mb-3">Exclusions</h3>
                    <div id={`${baseId}-exclusions-options`} className="tw-flex tw-items-center tw-space-x-8">
                        <label id={`${baseId}-exclusions-hide-label`} className="tw-flex tw-items-center">
                            <div id={`${baseId}-exclusions-hide-radio-wrapper`} className="tw-relative tw-mr-2 tw-w-5 tw-h-5 tw-flex tw-items-center tw-justify-center">
                                <input
                                    id={`${baseId}-exclusions-hide-radio`}
                                    data-testid={`${baseId}-exclusions-hide-radio`}
                                    type="radio"
                                    checked={exclusionType === 'hide'}
                                    onChange={() => handleConfigChange('exclusionType', 'hide')}
                                    className="tw-absolute tw-opacity-0 tw-h-5 tw-w-5"
                                />
                                <div className={`tw-rounded-full tw-border tw-border-[#D1D1D6] tw-w-5 tw-h-5 tw-flex tw-items-center tw-justify-center ${
                                    exclusionType === 'hide' ? 'tw-border-[#091CAA]' : ''
                                }`}>
                                    {exclusionType === 'hide' && (
                                        <div className="tw-bg-[#091CAA] tw-rounded-full tw-w-3 tw-h-3"></div>
                                    )}
                                </div>
                            </div>
                            <span id={`${baseId}-exclusions-hide-text`} className="tw-text-[#172B4D] tw-text-sm">Hide</span>
                        </label>

                        <label id={`${baseId}-exclusions-disable-label`} className="tw-flex tw-items-center">
                            <div id={`${baseId}-exclusions-disable-radio-wrapper`} className="tw-relative tw-mr-2 tw-w-5 tw-h-5 tw-flex tw-items-center tw-justify-center">
                                <input
                                    id={`${baseId}-exclusions-disable-radio`}
                                    data-testid={`${baseId}-exclusions-disable-radio`}
                                    type="radio"
                                    checked={exclusionType === 'disable'}
                                    onChange={() => handleConfigChange('exclusionType', 'disable')}
                                    className="tw-absolute tw-opacity-0 tw-h-5 tw-w-5"
                                />
                                <div className={`tw-rounded-full tw-border tw-border-[#D1D1D6] tw-w-5 tw-h-5 tw-flex tw-items-center tw-justify-center ${
                                    exclusionType === 'disable' ? 'tw-border-[#091CAA]' : ''
                                }`}>
                                    {exclusionType === 'disable' && (
                                        <div className="tw-bg-[#091CAA] tw-rounded-full tw-w-3 tw-h-3"></div>
                                    )}
                                </div>
                            </div>
                            <span id={`${baseId}-exclusions-disable-text`} className="tw-text-[#172B4D] tw-text-sm">Disable</span>
                        </label>
                    </div>
                </div>
            </div>
        </div>
    );
};
